import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { QUOTES } from "@/data/quotes";
import { useTheme } from "@/hooks/useTheme";

const INTERVAL_MS = 30_000;
const TOTAL = QUOTES.length;
const MAX_DOTS = 7;

type AnimState = "idle" | "exit" | "enter";

export default function QuoteCarousel() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [index, setIndex] = useState(() => Math.floor(Math.random() * TOTAL));
  const [anim, setAnim] = useState<AnimState>("idle");

  const indexRef = useRef(index);
  const animRef = useRef<AnimState>("idle");
  const pendingRef = useRef(index);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const syncIndex = (i: number) => {
    indexRef.current = i;
    setIndex(i);
  };

  const syncAnim = (a: AnimState) => {
    animRef.current = a;
    setAnim(a);
  };

  useEffect(() => {
    if (anim === "exit") {
      const t = setTimeout(() => {
        syncIndex(pendingRef.current);
        syncAnim("enter");
      }, 980);
      return () => clearTimeout(t);
    }
    if (anim === "enter") {
      const t = setTimeout(() => syncAnim("idle"), 1320);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [anim]);

  const startTransition = useCallback((nextIndex: number) => {
    if (animRef.current !== "idle") return;
    pendingRef.current = nextIndex;
    syncAnim("exit");
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (animRef.current !== "idle") return;
      const next = (indexRef.current + 1) % TOTAL;
      pendingRef.current = next;
      syncAnim("exit");
    }, INTERVAL_MS);
  }, []);

  useEffect(() => {
    resetTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goNext = () => {
    startTransition((indexRef.current + 1) % TOTAL);
    resetTimer();
  };

  const goPrev = () => {
    startTransition((indexRef.current - 1 + TOTAL) % TOTAL);
    resetTimer();
  };

  const goTo = (i: number) => {
    startTransition(i);
    resetTimer();
  };

  const quote = QUOTES[index]!;

  const glassBase = isDark
    ? "rgba(10, 10, 18, 0.84)"
    : "rgba(252, 252, 255, 0.86)";
  const borderColor = isDark
    ? "rgba(99, 102, 241, 0.20)"
    : "rgba(99, 102, 241, 0.12)";
  const topGlow = isDark
    ? "0 -1px 0 0 rgba(99,102,241,0.30), 0 -6px 28px -4px rgba(99,102,241,0.14)"
    : "0 -1px 0 0 rgba(99,102,241,0.18), 0 -4px 20px -4px rgba(99,102,241,0.08)";
  const textColor = isDark ? "rgba(232,232,255,0.88)" : "rgba(22,22,44,0.80)";
  const attrColor = isDark ? "rgba(170,170,220,0.48)" : "rgba(80,80,130,0.50)";
  const dotActive = isDark ? "rgba(130,140,255,0.88)" : "rgba(99,102,241,0.72)";
  const dotIdle = isDark ? "rgba(255,255,255,0.16)" : "rgba(99,102,241,0.16)";
  const arrowColor = isDark ? "rgba(170,180,255,0.60)" : "rgba(99,102,241,0.52)";
  const arrowHoverColor = isDark ? "rgba(210,215,255,0.92)" : "rgba(55,60,200,0.82)";

  const dotCount = Math.min(TOTAL, MAX_DOTS);
  const groupSize = Math.ceil(TOTAL / dotCount);
  const activeDot = Math.floor(index / groupSize);

  const animClass =
    anim === "exit" ? "quote-exit-right" :
    anim === "enter" ? "quote-enter-left" :
    "";

  return (
    <div
      role="region"
      aria-label="Inspiration quote"
      style={{
        width: "100%",
        background: glassBase,
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        borderTop: `1px solid ${borderColor}`,
        boxShadow: topGlow,
        padding: "28px 20px 24px",
      }}
    >
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minHeight: "72px",
          }}
        >
          <button
            onClick={goPrev}
            aria-label="Previous quote"
            style={{
              flexShrink: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: arrowColor,
              padding: "6px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={e => (e.currentTarget.style.color = arrowHoverColor)}
            onMouseLeave={e => (e.currentTarget.style.color = arrowColor)}
          >
            <ChevronLeft size={18} />
          </button>

          <div style={{ flex: 1, textAlign: "center", overflow: "hidden" }}>
            <div className={animClass}>
              <p
                style={{
                  fontSize: "clamp(13px, 1.4vw, 15px)",
                  fontWeight: 300,
                  fontStyle: "italic",
                  lineHeight: 1.70,
                  color: textColor,
                  margin: 0,
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: "0.012em",
                }}
              >
                "{quote.text}"
              </p>
              <p
                style={{
                  fontSize: "10.5px",
                  fontWeight: 400,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: attrColor,
                  margin: "8px 0 0",
                }}
              >
                — {quote.attribution}
              </p>
            </div>
          </div>

          <button
            onClick={goNext}
            aria-label="Next quote"
            style={{
              flexShrink: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: arrowColor,
              padding: "6px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={e => (e.currentTarget.style.color = arrowHoverColor)}
            onMouseLeave={e => (e.currentTarget.style.color = arrowColor)}
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "5px" }} aria-hidden>
          {Array.from({ length: dotCount }).map((_, i) => {
            const isActive = i === activeDot;
            return (
              <button
                key={i}
                onClick={() => goTo(i * groupSize)}
                aria-label={`Go to quote group ${i + 1}`}
                style={{
                  width: isActive ? "20px" : "5px",
                  height: "5px",
                  borderRadius: "3px",
                  background: isActive ? dotActive : dotIdle,
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  transition: "all 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
